import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Button, 
  Box, 
  CircularProgress, 
  Alert,
  Card,
  CardContent,
  Divider,
  TextField,
  Grid
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

interface ApiKeyResponse {
  success: boolean;
  message: string;
  business_name: string;
  business_id: string;
  api_key_partial: string;
  default_practitioner_id?: string;
  note?: string;
}

const APIKeyTest: React.FC = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<ApiKeyResponse | null>(null);
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [loadingPractitioners, setLoadingPractitioners] = useState(false);

  const testApiKey = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/test-api-key', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Add info about the default practitioner
      const defaultPractitionerId = response.data.default_practitioner_id;
      if (defaultPractitionerId) {
        response.data.note = `Default practitioner ID set to: ${defaultPractitionerId}`;
      }
      
      setResponse(response.data);
    } catch (err: any) {
      console.error('Error testing API key:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPractitioners = async () => {
    try {
      setLoadingPractitioners(true);
      setError(null);
      
      const response = await axios.get('/api/practitioners', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Sort practitioners by name for easier reading
      const sortedPractitioners = [...(response.data.practitioners || [])].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setPractitioners(sortedPractitioners);
      
      // If we found practitioners, update the note to remind user how to filter
      if (sortedPractitioners.length > 0) {
        setResponse(prev => prev ? {
          ...prev,
          note: "To filter calendar by practitioner, use the practitioner ID in calendar views"
        } : prev);
      }
    } catch (err: any) {
      console.error('Error fetching practitioners:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingPractitioners(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Cliniko API Key Test
      </Typography>
      
      <Typography variant="body1" paragraph>
        This page allows you to test the Cliniko API key configuration. Click the button below to verify the API key and see which Cliniko business account is connected.
      </Typography>
      
      <Box sx={{ mt: 3, mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={testApiKey}
          disabled={loading}
          sx={{ mr: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Test API Key'}
        </Button>

        <Button 
          variant="outlined" 
          color="secondary" 
          onClick={fetchPractitioners}
          disabled={loadingPractitioners}
        >
          {loadingPractitioners ? <CircularProgress size={24} /> : 'Fetch Practitioners'}
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {response && (
        <Card variant="outlined" sx={{ mt: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              API Key Test Results
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="subtitle2">Status:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2" color={response.success ? "success.main" : "error.main"}>
                  {response.success ? "Valid" : "Invalid"}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              <Grid item xs={4}>
                <Typography variant="subtitle2">Business Name:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2">
                  {response.business_name}
                </Typography>
              </Grid>
              
              <Grid item xs={4}>
                <Typography variant="subtitle2">Business ID:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2">
                  {response.business_id}
                </Typography>
              </Grid>
              
              <Grid item xs={4}>
                <Typography variant="subtitle2">API Key:</Typography>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={response.api_key_partial}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              
              {response.note && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {response.note}
                    </Alert>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
      
      {practitioners.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Practitioners ({practitioners.length})
            </Typography>
            
            {practitioners.map((practitioner) => (
              <Box 
                key={practitioner.id} 
                sx={{ 
                  mb: 2, 
                  p: 1.5, 
                  border: '1px solid #eee', 
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
                onClick={() => {
                  // Copy to clipboard
                  navigator.clipboard.writeText(practitioner.id);
                  alert(`Copied practitioner ID: ${practitioner.id}`);
                }}
              >
                <Typography variant="subtitle1">
                  {practitioner.first_name} {practitioner.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {practitioner.id} (click to copy)
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Paper>
  );
};

export default APIKeyTest;